import { createApi, fetchBaseQuery, BaseQueryFn } from "@reduxjs/toolkit/query/react";
import { getApiConfig } from "@/config/api";
import { getRequestLanguage } from "@/utils/requestLanguage";
import type { ApiResponse } from "@/types";

export interface CashDrawer {
  id: string;
  outletDrawerId: string;
  userIdOpened: string;
  userIdClosed?: string;
  currentBalance: number;
  openingBalance: number;
  closingBalance?: number;
  status: "OPEN" | "CLOSED";
  openedAt: string;
  closedAt?: string;
  createdAt: string;
  updatedAt: string;
  outletDrawer?: {
    id: string;
    name: string;
  };
  userOpened?: {
    name: string;
    username: string;
  };
  userClosed?: {
    name: string;
    username: string;
  };
}

export interface CashTransaction {
  id: string;
  cashDrawerId: string;
  userId: string;
  type: "SALE" | "EXPENSE" | "ADJUSTMENT" | "OPENING_BALANCE" | "CLOSING_BALANCE";
  amount: number;
  balanceBefore: number;
  balanceAfter: number;
  reference?: string;
  referenceType?: string;
  description?: string;
  createdAt: string;
}

export interface CashDrawerWithAnalytics extends CashDrawer {
  userOpened: {
    name: string;
    username: string;
  };
  outletDrawer: {
    id: string;
    name: string;
  };
  transactions: CashTransaction[];
  analytics: {
    todaysSales: {
      amount: number;
      count: number;
    };
    todaysExpenses: {
      amount: number;
      count: number;
    };
    expectedBalance: number;
  };
}

export interface OpenDrawerParams {
  outletDrawerId: string;
  openingBalance: number;
  description?: string;
  outletId: string;
  storeId: string;
}

export interface CloseDrawerParams {
  outletDrawerId: string;
  countedCash: number;
  description?: string;
  outletId: string;
  storeId: string;
}

export interface AdjustBalanceParams {
  amount: number;
  description: string;
  type: "ADJUSTMENT" | "EXPENSE";
  outletId: string;
  storeId: string;
}

export interface RecordCashSaleParams {
  transactionId: string;
  amount: number;
}

export interface CashTransactionHistoryParams {
  page?: number;
  limit?: number;
  startDate?: string;
  endDate?: string;
  type?: string;
}

export interface DrawerHistoryParams {
  page?: number;
  limit?: number;
}

export interface CashAnalyticsParams {
  period?: "7d" | "30d" | "90d";
}

export interface DrawerStatus {
  id: string;
  name: string;
  description: string | null;
  isOpen: boolean;
  currentSession: {
    id: string;
    currentBalance: number;
    openedAt: string;
    userOpened: {
      id: string;
      name: string;
      username: string;
    };
  } | null;
}

export interface CashAnalytics {
  currentDrawer: {
    id: string;
    currentBalance: number;
    openingBalance: number;
    openedAt: string;
  } | null;
  periodAnalytics: Record<string, { count: number; total: number }>;
  dailyBreakdown: Array<{
    date: string;
    sales: number;
    expenses: number;
    adjustments: number;
  }>;
  summary: {
    totalSales: number;
    totalExpenses: number;
    totalAdjustments: number;
    netCashFlow: number;
  };
}

// Create a dynamic base query for cash drawer API
const dynamicCashDrawerBaseQuery: BaseQueryFn = async (args, api, extraOptions) => {
  const config = getApiConfig();

  const baseQuery = fetchBaseQuery({
    baseUrl: config.API_URL,
    prepareHeaders: (headers, { getState }) => {
      let token = (getState() as any).auth.token;
      if (!token) {
        token = localStorage.getItem("token");
      }
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }
      headers.set("accept-language", getRequestLanguage());
      return headers;
    },
  });

  return baseQuery(args, api, extraOptions);
};

export const cashDrawerApi = createApi({
  reducerPath: "cashDrawerApi",
  baseQuery: dynamicCashDrawerBaseQuery,
  tagTypes: ["CashDrawer", "CashTransaction"],
  endpoints: builder => ({
    // Get all drawer statuses for outlet
    getDrawerStatuses: builder.query<
      ApiResponse<DrawerStatus[]>,
      { outletId: string; storeId: string }
    >({
      query: ({ outletId, storeId }) => {
        const searchParams = new URLSearchParams();
        searchParams.append("outletId", outletId);
        searchParams.append("storeId", storeId);
        return `/cash-drawer/statuses?${searchParams.toString()}`;
      },
      providesTags: ["CashDrawer"],
    }),

    // Get current cash drawer status
    getCurrentDrawer: builder.query<
      ApiResponse<CashDrawerWithAnalytics | null>,
      { outletId: string; storeId: string }
    >({
      query: ({ outletId, storeId }) => {
        const searchParams = new URLSearchParams();
        searchParams.append("outletId", outletId);
        searchParams.append("storeId", storeId);
        return `/cash-drawer/current?${searchParams.toString()}`;
      },
      providesTags: ["CashDrawer"],
    }),

    // Check drawer status (for validation)
    checkDrawerStatus: builder.query<
      ApiResponse<{ isOpen: boolean; drawer: CashDrawer | null }>,
      { outletId: string; storeId: string }
    >({
      query: ({ outletId, storeId }) => {
        const searchParams = new URLSearchParams();
        searchParams.append("outletId", outletId);
        searchParams.append("storeId", storeId);
        return `/cash-drawer/status?${searchParams.toString()}`;
      },
      providesTags: ["CashDrawer"],
    }),

    // Open cash drawer
    openDrawer: builder.mutation<ApiResponse<CashDrawer>, OpenDrawerParams>({
      query: data => ({
        url: "/cash-drawer/open",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["CashDrawer", "CashTransaction"],
    }),

    // Close cash drawer
    closeDrawer: builder.mutation<
      ApiResponse<CashDrawer & { variance: number }>,
      CloseDrawerParams
    >({
      query: data => ({
        url: "/cash-drawer/close",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["CashDrawer", "CashTransaction"],
    }),

    // Adjust cash balance
    adjustBalance: builder.mutation<ApiResponse<CashDrawer>, AdjustBalanceParams>({
      query: data => ({
        url: "/cash-drawer/adjust",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["CashDrawer", "CashTransaction"],
    }),

    // Record cash sale (internal use)
    recordCashSale: builder.mutation<ApiResponse<CashDrawer>, RecordCashSaleParams>({
      query: data => ({
        url: "/cash-drawer/record-sale",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["CashDrawer", "CashTransaction"],
    }),

    // Get cash transaction history
    getCashTransactionHistory: builder.query<
      ApiResponse<{
        transactions: CashTransaction[];
        pagination: {
          page: number;
          limit: number;
          total: number;
          pages: number;
        };
      }>,
      CashTransactionHistoryParams
    >({
      query: params => {
        const searchParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== "") {
            searchParams.append(key, value.toString());
          }
        });
        return `/cash-drawer/transactions?${searchParams.toString()}`;
      },
      providesTags: ["CashTransaction"],
    }),

    // Get drawer history
    getDrawerHistory: builder.query<
      ApiResponse<{
        drawers: Array<
          CashDrawer & {
            user: { name: string; username: string };
            _count: { transactions: number };
            analytics: {
              salesAmount: number;
              salesCount: number;
              expensesAmount: number;
              expensesCount: number;
              variance: number | null;
            };
          }
        >;
        pagination: {
          page: number;
          limit: number;
          total: number;
          pages: number;
        };
      }>,
      DrawerHistoryParams
    >({
      query: params => {
        const searchParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== "") {
            searchParams.append(key, value.toString());
          }
        });
        return `/cash-drawer/history?${searchParams.toString()}`;
      },
      providesTags: ["CashDrawer"],
    }),

    // Get cash analytics
    getCashAnalytics: builder.query<ApiResponse<CashAnalytics>, CashAnalyticsParams>({
      query: params => {
        const searchParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== "") {
            searchParams.append(key, value.toString());
          }
        });
        return `/cash-drawer/analytics?${searchParams.toString()}`;
      },
      providesTags: ["CashDrawer", "CashTransaction"],
    }),
  }),
});

export const {
  useGetDrawerStatusesQuery,
  useGetCurrentDrawerQuery,
  useCheckDrawerStatusQuery,
  useOpenDrawerMutation,
  useCloseDrawerMutation,
  useAdjustBalanceMutation,
  useRecordCashSaleMutation,
  useGetCashTransactionHistoryQuery,
  useGetDrawerHistoryQuery,
  useGetCashAnalyticsQuery,
} = cashDrawerApi;
